<script setup>
import { ref } from 'vue'
import TeamSearchInput from './TeamSearchInput.vue'
import DateTimePickerCustom from './DateTimePickerCustom.vue'
import axios from 'axios'
import { useFixtureStore } from '../stores/fixtureStore'
import { useMenuStore } from '../stores/menuStore'
import { useUserStore } from '../stores/userStore'

const fixtureStore = useFixtureStore()
const menuStore = useMenuStore()
const userStore = useUserStore()

// Define emits for parent communication
const emit = defineEmits(['fixtureCreated'])

const isSubmitting = ref(false)
const error = ref(null)

const formData = ref({
  datetime: '',
  homeTeam: '',
  awayTeam: '',
  homeTeamId: null,
  awayTeamId: null
})

const handleSubmit = async () => {
  isSubmitting.value = true
  error.value = null

  try {
    // Split datetime into date and time
    const [date, time] = formData.value.datetime.split(', ')

    // Convert local datetime to UTC
    let date_time_utc = ''
    
    if (date && time) {
      // Parse local date and time (format: "dd.mm.yyyy, HH:mm")
      const [day, month, year] = date.split('.')
      const [hour, minute] = time.split(':')
      
      // Create Date object in local timezone
      const localDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hour),
        parseInt(minute)
      )
      
      // Convert to UTC and format as ISO 8601: "YYYY-MM-DDTHH:mm:ssZ"
      const utcYear = localDate.getUTCFullYear()
      const utcMonth = (localDate.getUTCMonth() + 1).toString().padStart(2, '0')
      const utcDay = localDate.getUTCDate().toString().padStart(2, '0')
      const utcHour = localDate.getUTCHours().toString().padStart(2, '0')
      const utcMinute = localDate.getUTCMinutes().toString().padStart(2, '0')
      const utcSecond = localDate.getUTCSeconds().toString().padStart(2, '0')
      
      // Format UTC datetime as ISO 8601: "YYYY-MM-DDTHH:mm:ssZ"
      date_time_utc = `${utcYear}-${utcMonth}-${utcDay}T${utcHour}:${utcMinute}:${utcSecond}Z`
    }

    const payload = {
      date,
      time,
      date_time_utc,
      home_team_id: formData.value.homeTeamId,
      away_team_id: formData.value.awayTeamId,
      league_id: menuStore.thirdMenuContent?.id
    }

    const { data } = await axios.post('/api/fixtures/generate', payload)
    
    // Handle successful response
    console.log('Fixture generated:', data)
    
    // Emit the new fixture to parent component so it can be added to the list
    if (data && data.fixture) {
      emit('fixtureCreated', data.fixture)
    }
    
    // Reset form
    formData.value = {
      datetime: '',
      homeTeam: '',
      awayTeam: '',
      homeTeamId: null,
      awayTeamId: null
    }
  } catch (err) {
    error.value = err.response?.data?.message || 'An error occurred while generating the fixture'
    console.error('Error generating fixture:', err)
  } finally {
    isSubmitting.value = false
  }
}

const handleTeamSelect = (team, type) => {
  formData.value[`${type}TeamId`] = team.id
}
</script>

<template>
  <div class="bg-white rounded-lg shadow mb-6">
    <div v-if="userStore.currentUser?.role === 'admin'" class="p-6">
      <h3 class="text-lg font-semibold mb-4">Create New Fixture</h3>
      
      <div class="space-y-6">
        <div>
          <label class="block text-sm text-gray-500 mb-1">Match Date & Time</label>
          <DateTimePickerCustom
            v-model="formData.datetime"
            placeholder="Select date and time"
          />
        </div>

        <div class="grid grid-cols-[1fr_auto_1fr] gap-6 items-end">
          <div>
            <label class="block text-sm text-gray-500 mb-1">Home Team</label>
            <TeamSearchInput
              v-model="formData.homeTeam"
              placeholder="Search home team..."
              @select="(team) => handleTeamSelect(team, 'home')"
            />
          </div>
          <div class="flex items-center justify-center pb-2">
            <span class="text-lg font-medium text-gray-500">VS</span>
          </div>
          <div>
            <label class="block text-sm text-gray-500 mb-1">Away Team</label>
            <TeamSearchInput
              v-model="formData.awayTeam"
              placeholder="Search away team..."
              @select="(team) => handleTeamSelect(team, 'away')"
            />
          </div>
        </div>

        <!-- Error message -->
        <div v-if="error" class="text-red-500 text-sm mt-2">
          {{ error }}
        </div>

        <!-- Submit Button -->
        <div class="flex justify-end mt-6">
          <button 
            @click="handleSubmit"
            :disabled="isSubmitting"
            class="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <span v-if="isSubmitting" class="mr-2">
              <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            {{ isSubmitting ? 'Generating...' : 'Generate Fixture' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template> 